import React from 'react';
import axios from 'axios';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import { SocketIO } from 'boardgame.io/multiplayer';
import { Client } from 'boardgame.io/react';
import Cookies from 'js-cookie';
import { Tooltip } from 'react-tooltip';

import { ITCG } from './game';
import { ITCGBoard } from './itcgBoard';
import {
  ITCGCards,
  ITCGDeck,
  ITCGHeader,
  ITCGSignUp,
  ITCGLogIn,
  ITCGLogOut,
  ITCGRoom,
  ITCGGameStats,
  ITCGFrontPage,
  ITCGHowToPlay,
  ITCGResources,
  ITCGFrontPost,
  ITCGPosts,
  ITCGWritePosts,
  ITCGPostStand,
} from './lobby';
import { SERVER, MAX_WIDTH, ROLES_COOKIE_NAME, USER_COOKIE_NAME } from './config';

const style: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  verticalAlign: 'middle',
  overflowY: 'auto',
  backgroundColor: '#FAF8F0',
  height: '100vh',
};

const clientStyle: React.CSSProperties = {
  width: '100vw',
};

export interface GamePlayerData {
  matchID: string;
  playerID?: string;
  credentials?: string;
}

export interface AppState {
  username?: string;
  roles?: string[];
  inGame?: GamePlayerData;
}

const tooltipStyle: React.CSSProperties = {
  zIndex: 4,
  padding: '0.1em',
  maxWidth: '50vh',
};

class App extends React.Component {
  state: AppState;

  constructor(prop: any) {
    super(prop);

    const state: AppState = {
      username: Cookies.get(USER_COOKIE_NAME),
    };
    const cookieRoles = Cookies.get(ROLES_COOKIE_NAME);
    if (cookieRoles) state.roles = JSON.parse(cookieRoles);

    this.state = state;
  }

  async componentDidMount() {
    if (this.state.username) await this.updateState({});
  }

  async componentDidUpdate() {
    if (this.state.username && !this.state.roles) {
      this.setState({
        roles: JSON.parse(Cookies.get(ROLES_COOKIE_NAME) ?? '[]'),
      });
    }
  }

  async updateState(state: AppState) {
    const resp = await axios
      .get('/lobby/inGame', {
        baseURL: SERVER,
        timeout: 8000,
        withCredentials: true,
      })
      .catch((err) => {
        if (err.response.status === 422) {
          // Auth Cookie expired; logout user to refresh
          return this.setState({ username: '', inGame: undefined });
        } else {
          console.error(err);
        }
      });

    if (resp && resp.data) {
      return this.setState({ ...state, inGame: resp.data });
    } else if (!!state.inGame) {
      return this.setState(state);
    }

    this.setState({ ...state, inGame: undefined });
  }

  render() {
    const GameClient = Client({
      game: ITCG,
      board: ITCGBoard,
      multiplayer: SocketIO({ server: SERVER }),
      debug: false,
    });

    return (
      <>
        <Tooltip id="expanded-card" style={tooltipStyle} opacity={1} />
        <BrowserRouter>
          {this.state.username === '' && <Redirect to={'/logout'} />}
          {!this.state.inGame && (
            <div style={style}>
              <ITCGHeader
                username={this.state.username}
                isAdmin={this.state.roles?.includes('admin')}
              />
              <div style={{ maxWidth: MAX_WIDTH, width: '100%' }}>
                <Switch>
                  <Route path={'/decks'}>
                    {this.state.username && <ITCGDeck server={SERVER} />}
                  </Route>
                  <Route path={'/stats'}>
                    {this.state.username && this.state.roles?.includes('admin') && (
                      <ITCGGameStats server={SERVER} />
                    )}
                  </Route>
                  <Route path={'/howtoplay'}>
                    <ITCGHowToPlay />
                  </Route>
                  <Route path={'/cards'}>
                    <ITCGCards />
                  </Route>
                  <Route path={'/posts/write/:id'}>
                    {this.state.username && (
                      <ITCGWritePosts server={SERVER} username={this.state.username} />
                    )}
                  </Route>
                  <Route path={'/posts/write'}>
                    {this.state.username && this.state.roles?.includes('editor') && (
                      <ITCGWritePosts server={SERVER} username={this.state.username} />
                    )}
                  </Route>
                  <Route path={'/posts/:idcat'}>
                    <ITCGPosts server={SERVER} username={this.state.username} />
                  </Route>
                  <Route exact path={'/posts'}>
                    <ITCGFrontPost
                      server={SERVER}
                      isEditor={this.state.roles?.includes('editor')}
                    />
                  </Route>
                  <Route path={'/about'}>
                    <ITCGResources />
                  </Route>
                  <Route path={'/signup'}>
                    <ITCGSignUp server={SERVER} update={this.updateState.bind(this)} />
                  </Route>
                  <Route path={'/login'}>
                    <ITCGLogIn server={SERVER} update={this.updateState.bind(this)} />
                  </Route>
                  <Route path={'/logout'}>
                    <ITCGLogOut server={SERVER} update={this.updateState.bind(this)} />
                  </Route>
                  <Route path={'/'}>
                    {!this.state.username && <ITCGFrontPage />}
                    {this.state.username && (
                      <>
                        <ITCGRoom
                          server={SERVER}
                          username={this.state.username}
                          update={this.updateState.bind(this)}
                        />
                        <ITCGPostStand server={SERVER} />
                      </>
                    )}
                  </Route>
                </Switch>
              </div>
            </div>
          )}
          {!!this.state.inGame && !!this.state.inGame.playerID && (
            <div style={clientStyle}>
              <GameClient
                matchID={this.state.inGame.matchID}
                playerID={this.state.inGame.playerID}
                credentials={this.state.inGame.credentials}
              />
            </div>
          )}
          {!!this.state.inGame && !this.state.inGame.playerID && (
            <div style={clientStyle}>
              <GameClient matchID={this.state.inGame.matchID} />
            </div>
          )}
        </BrowserRouter>
      </>
    );
  }
}

export default App;
